import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Rating,
  TextField,
  Button,
  IconButton,
  Collapse,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Fade,
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Close as CloseIcon,
  Feedback as FeedbackIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// Tipos de features disponíveis para feedback
export type FeedbackFeature = 
  | 'document_editor'
  | 'document_improvement'
  | 'document_creation'
  | 'jurisprudence_search'
  | 'chat'
  | 'thesis_search'
  | 'template'
  | 'payment'
  | 'general';

export type FeedbackType = 'general' | 'text_selection';

interface FeedbackWidgetProps {
  // Feature que está recebendo feedback
  feature: FeedbackFeature;
  
  // Entidade vinculada (opcional)
  entityType?: string;
  entityId?: string;
  
  // Posição do widget
  position?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right' | 'inline';
  
  // Título personalizado
  title?: string;
  
  // Callbacks
  onFeedbackSubmitted?: (rating: number, comment: string) => void;
  onClose?: () => void;
  
  // Modo compacto (apenas ícone que expande)
  compact?: boolean;
  
  // Texto selecionado (para feedback de texto)
  selectedText?: string;
  textContext?: string;
  
  // Controle externo de visibilidade
  open?: boolean;
  
  // Estilo customizado
  sx?: object;
}

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  feature,
  entityType,
  entityId,
  position = 'bottom-left',
  title = 'Avalie esta funcionalidade',
  onFeedbackSubmitted,
  onClose,
  compact = true,
  selectedText,
  textContext,
  open: controlledOpen,
  sx,
}) => {
  const [isOpen, setIsOpen] = useState(controlledOpen ?? false);
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoverRating, setHoverRating] = useState(-1);

  // Usar controlledOpen se fornecido
  const effectiveOpen = controlledOpen !== undefined ? controlledOpen : isOpen;

  const handleOpen = () => {
    if (controlledOpen === undefined) {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    if (controlledOpen === undefined) {
      setIsOpen(false);
    }
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!rating) {
      setError('Por favor, selecione uma avaliação');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const feedbackData = {
        feature,
        feedback_type: selectedText ? 'text_selection' : 'general',
        rating,
        comment: comment.trim() || null,
        entity_type: entityType,
        entity_id: entityId,
        selected_text: selectedText,
        text_context: textContext,
      };

      await api.post('/feedback/', feedbackData);
      
      setSuccess(true);
      onFeedbackSubmitted?.(rating, comment);
      
      // Reset após sucesso
      setTimeout(() => {
        setRating(null);
        setComment('');
        setSuccess(false);
        handleClose();
      }, 2000);
      
    } catch (err: any) {
      console.error('Erro ao enviar feedback:', err);
      setError(err.response?.data?.detail || 'Erro ao enviar feedback');
    } finally {
      setLoading(false);
    }
  };

  // Labels para as estrelas
  const ratingLabels: Record<number, string> = {
    1: 'Muito ruim',
    2: 'Ruim',
    3: 'Regular',
    4: 'Bom',
    5: 'Excelente',
  };

  // Posicionamento do widget
  const getPositionStyles = () => {
    if (position === 'inline') return {};
    
    const styles: any = {
      position: 'fixed',
      zIndex: 1000,
    };
    
    switch (position) {
      case 'bottom-left':
        styles.bottom = 20;
        styles.left = 20;
        break;
      case 'bottom-right':
        styles.bottom = 20;
        styles.right = 20;
        break;
      case 'top-left':
        styles.top = 80;
        styles.left = 20;
        break;
      case 'top-right':
        styles.top = 80;
        styles.right = 20;
        break;
    }
    
    return styles;
  };

  return (
    <Box sx={{ ...getPositionStyles(), ...sx }}>
      {/* Botão compacto para abrir */}
      {compact && !effectiveOpen && (
        <Fade in={!effectiveOpen}>
          <Tooltip title="Deixe seu feedback">
            <IconButton
              onClick={handleOpen}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                boxShadow: 3,
              }}
            >
              <FeedbackIcon />
            </IconButton>
          </Tooltip>
        </Fade>
      )}

      {/* Widget expandido */}
      <Collapse in={effectiveOpen} timeout={300}>
        <Paper
          elevation={4}
          sx={{
            p: 2,
            width: 320,
            borderRadius: 2,
            backgroundColor: 'background.paper',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              {title}
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Texto selecionado (se houver) */}
          {selectedText && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: 'grey.100',
                borderRadius: 1,
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Texto selecionado:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                "{selectedText.length > 150 ? `${selectedText.substring(0, 150)}...` : selectedText}"
              </Typography>
            </Box>
          )}

          {/* Rating */}
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Rating
              name="feedback-rating"
              value={rating}
              onChange={(_, newValue) => setRating(newValue)}
              onChangeActive={(_, newHover) => setHoverRating(newHover)}
              size="large"
              emptyIcon={<StarBorderIcon fontSize="inherit" />}
              icon={<StarIcon fontSize="inherit" />}
              sx={{
                '& .MuiRating-iconFilled': {
                  color: 'primary.main',
                },
                '& .MuiRating-iconHover': {
                  color: 'primary.light',
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {rating !== null ? ratingLabels[hoverRating !== -1 ? hoverRating : rating] : 'Clique para avaliar'}
            </Typography>
          </Box>

          {/* Comentário */}
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Deixe um comentário (opcional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 2000 }}
          />

          {/* Botão de enviar */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !rating}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          >
            {loading ? 'Enviando...' : success ? 'Enviado!' : 'Enviar Feedback'}
          </Button>

          {/* Mensagem de sucesso */}
          {success && (
            <Typography
              variant="caption"
              color="success.main"
              display="block"
              textAlign="center"
              mt={1}
            >
              ✓ Obrigado pelo seu feedback!
            </Typography>
          )}
        </Paper>
      </Collapse>

      {/* Snackbar de erro */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedbackWidget;

